import React from 'react'
import moment from 'moment'
import {BadgeStatePolicy} from './BadgeStatePolicy'
import { Badge } from 'react-bootstrap'
import {STATUS_POLICY,   getDocumentVariant, getPaymentVariant} from '../../../helpers/consts'
import {useGetUser} from '../../../store/userStore'

interface PolicySummaryProps {
  updateStatuses: () => void
  assignAgent: () => void
  changeSource: () => void
  setTypeAssign: React.Dispatch<React.SetStateAction<'ASSIGN_TO' | 'USER_TO'>>
  data: any
  policy: string
  isQuote?: boolean
  category?: string | null
}

export const PolicySummary: React.FC<PolicySummaryProps> = ({
  updateStatuses,
  assignAgent,
  changeSource,
  setTypeAssign,
  data,
  policy,
  isQuote,
  category
}) => {
  const primaryGroup = useGetUser((s) => s.user?.user?.primaryGroup)
  const isAgent = primaryGroup?.name === 'Agente'
  const isAsesor = primaryGroup?.name === 'Asesor'
  const canViewSource = !isAgent  // Everyone except Agentes can view
  const canEditSource = !isAgent && !isAsesor  // Only Supervisores/Admins can edit
  const showDocumentsDueDate = data.stateDocs === 'PENDING' && !!data.documentsDueDate
  const daysUntilDocumentsDueDate = showDocumentsDueDate
    ? moment(data.documentsDueDate).startOf('day').diff(moment().startOf('day'), 'days')
    : null
  const documentsDueDateIsOverdue = daysUntilDocumentsDueDate !== null && daysUntilDocumentsDueDate < 0

  return (
  <div>
    <div className='kt-heading kt-heading--sm custom-border-brand my-3'>
      {isQuote ? 'Quote' : 'Policy'} Summary
    </div>
    <div className='kt-widget__info'>
      <div className='policy-status-edit-modal'>
        <span className='kt-widget__label '>Agent (Owner):</span>{' '}
        <i
          onClick={() => {
            setTypeAssign('USER_TO')
            assignAgent()
          }}
          className='bi bi-person-bounding-box set-important'
        ></i>
      </div>
      <span className='kt-widget__data'>
        {data.user?.firstName} {data.user?.lastName}
      </span>
    </div>
    <div className='kt-widget__info'>
      <div className='policy-status-edit-modal'>
        <span className='kt-widget__label '>Assign To:</span>{' '}
        <i
          onClick={() => {
            setTypeAssign('ASSIGN_TO')
            assignAgent()
          }}
          className='bi bi-person-bounding-box set-important'
        ></i>
      </div>
      <span className='kt-widget__data'>
        {data.assignedTo?.firstName} {data.assignedTo?.lastName}
      </span>
    </div>
    <div className='kt-widget__info'>
      <div className='policy-status-edit-modal'>
        <span className='kt-widget__label '>Created by:</span>{' '}
      </div>
      <span className='kt-widget__data'>
        {data.createdBy?.firstName} {data.createdBy?.lastName}
      </span>
    </div>
    <div className='kt-widget__info'>
      <span className='kt-widget__label'>Internal code:</span>
      <span className='kt-widget__data'>{policy}</span>
    </div>
    <div className='kt-widget__info'>
      <span className='kt-widget__label'>Carrier:</span>
      <span className='kt-widget__data'>{data?.carrier?.name || 'N/R'}</span>
    </div>
    <div className='kt-widget__info'>
      <span className='kt-widget__label'>Effective:</span>
      <span className='kt-widget__data' id='effective_label'>
        {data.effectiveDate ? moment(data.effectiveDate).format('DD MMM, YY') : 'N/R'}
      </span>
    </div>
    <div className='kt-widget__info'>
      <span className='kt-widget__label'>No. of applicants:</span>
      <span className='kt-widget__data' id='no_applicants'>
        {data.applicants ? Number(data.applicants) : 'N/R'}
      </span>
    </div>
    <div className='kt-widget__info'>
      <div className='policy-status-edit-modal'>
        <span className='kt-widget__label '>Status:</span>{' '}
        {!isQuote && <i onClick={updateStatuses} className='bi bi-pencil-square set-important'></i>}
      </div>
      <span className='kt-widget__data'>
        <BadgeStatePolicy status={data.status} color={data.color} />
      </span>
    </div>
    {category !== STATUS_POLICY.PENDING && (
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>
          {category === STATUS_POLICY.CANCELED
            ? 'Canceled'
            : category === STATUS_POLICY.COMPLETED
            ? 'Completed'
            : category}
          {': '}
        </span>
        <span className='kt-widget__data'>
          {isQuote
            ? moment(data.created).format('DD MMM YYYY')
            : moment(data.canceledDate || data.completedDate).format('DD MMM YYYY')}
        </span>
      </div>
    )}
    <div className='kt-widget__info'>
      <span className='kt-widget__label'>Documents status:</span>
      <span className='kt-widget__data'>
        <Badge className="badge-sm" bg={getDocumentVariant(data.stateDocs)}>
          {data.stateDocs || 'N/A'}
        </Badge>
      </span>
    </div>
    {showDocumentsDueDate && (
      <div className='kt-widget__info'>
        <span className='kt-widget__label'>Docs due date:</span>
        <span className='kt-widget__data'>
          <Badge
            className="badge-sm"
            bg={documentsDueDateIsOverdue ? 'danger' : 'warning'}
            text={documentsDueDateIsOverdue ? 'white' : 'dark'}
          >
            {moment(data.documentsDueDate).format('DD MMM, YY')}
            {daysUntilDocumentsDueDate !== null &&
              ` (${daysUntilDocumentsDueDate >= 0 ? `${daysUntilDocumentsDueDate} days left` : `${Math.abs(daysUntilDocumentsDueDate)} days overdue`})`}
          </Badge>
        </span>
      </div>
    )}
    <div className='kt-widget__info'>
      <span className='kt-widget__label'>Payment status:</span>
      <span className='kt-widget__data'>
        <Badge
          className="badge-sm"
          bg={getPaymentVariant(data.paymentStatus)}
          text={getPaymentVariant(data.paymentStatus) === 'danger' ? 'white' : undefined}
        >
          {data.paymentStatus || 'N/A'}
        </Badge>
      </span>
    </div>
    <div className='kt-widget__info'>
      <span className='kt-widget__label'>Last update:</span>
      <span className='kt-widget__data' id='last_update_label'>
        {data.updated ? moment(data.updated).format('DD MMM, YY') : 'N/R'}
      </span>
    </div>
    {canViewSource && (
      <div className='kt-widget__info'>
        <div className='policy-status-edit-modal'>
          <span className='kt-widget__label '>Source:</span>{' '}
          {canEditSource && (
            <i onClick={changeSource} className='bi bi-pencil-square set-important'></i>
          )}
        </div>
        <span className='kt-widget__data'>
          {data.leadSource?.name || 'N/R'}
        </span>
      </div>
    )}
  </div>
  )
}
